import React from 'react';
import MessageHistoryDropdown from '../MessageHistoryDropdown';
import CommandSuggestions from '../CommandSuggestions';
import { ChatMessage, Tool } from 'models/chatbot';
import { ReactComponent as MicrophoneIcon } from '../../svg/microphone.svg';
import { ReactComponent as MicrophoneIconFillled } from '../../svg/microphone_filled.svg';
import { ReactComponent as SquareIcon } from '../../svg/square.svg';
import { ReactComponent as PlaneIconFilled } from '../../svg/plane_filled.svg';
import { ReactComponent as PlaneIcon } from '../../svg/plane_empty.svg';
import { Button } from '../Button';
import { getAutocompleteDisplayText, getTextWidth } from '../../helpers/utils';
import useVoiceRecording from '../../hooks/useVoiceRecording';
import { AutocompleteText, InputRowContainer, StyledInput } from './styles';
import useChatbot from 'hooks/useChatbot';
import utils from 'utils';

interface CommandArgument {
  name: string;
  description: string;
  placeholder?: string;
  required?: boolean;
  type?: string;
}

interface Command {
  name: string;
  description: string;
  arguments?: CommandArgument[];
}

interface InputRowComponentProps {
  showMessageHistory: boolean;
  showCommandSuggestions: boolean;
  commandSuggestions: Command[];
  question: string;
  loading: boolean;
  autocompleteSuggestion: string | null;
  selectedHistoryIndex: number;
  inputRef: React.RefObject<HTMLInputElement>;
  tools: Tool[];
  getLastUserMessages: () => ChatMessage[];
  setQuestion: (question: string) => void;
  setShowMessageHistory: (show: boolean) => void;
  selectCommand: (commandName: string) => string;
  setQuestionFromCommand: (fullCommand: string) => void;
  hideCommandSuggestions: () => void;
  handleInputChange: (value: string, tools: Tool[]) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleAsk: () => void;
  abort: () => void;
}

export const InputRowComponent: React.FC<InputRowComponentProps> = ({
  showMessageHistory,
  showCommandSuggestions,
  commandSuggestions,
  question,
  loading,
  autocompleteSuggestion,
  selectedHistoryIndex,
  inputRef,
  tools,
  getLastUserMessages,
  setQuestion,
  setShowMessageHistory,
  selectCommand,
  setQuestionFromCommand,
  hideCommandSuggestions,
  handleInputChange,
  handleKeyDown,
  handleAsk,
  abort,
}) => {
  const { isRecording, startRecording, stopRecording } = useVoiceRecording({
    onTranscript: setQuestion,
  });
  const t = utils.intl.useTranslator();

  const { mode } = useChatbot();

  return (
    <InputRowContainer isEmbedded={mode === 'embedded'}>
      {showMessageHistory && (
        <MessageHistoryDropdown
          messages={getLastUserMessages()}
          onSelect={(message) => {
            setQuestion(message.question);
            setShowMessageHistory(false);
          }}
          onClose={() => setShowMessageHistory(false)}
          selectedIndex={selectedHistoryIndex}
        />
      )}

      {showCommandSuggestions && commandSuggestions.length > 0 && (
        <CommandSuggestions
          commands={commandSuggestions}
          onSelect={(commandName) => {
            const fullCommand = selectCommand(commandName);
            setQuestionFromCommand(fullCommand);

            if (inputRef.current) {
              inputRef.current.focus();
            }
          }}
          onClose={() => hideCommandSuggestions()}
        />
      )}

      <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center' }}>
        <StyledInput
          value={question}
          onChange={(e) => {
            setQuestion(e.target.value);
            handleInputChange(e.target.value, tools);
          }}
          onKeyDown={handleKeyDown}
          placeholder={utils.app.textTranslated('typeYourMessage', t, 'Type your message or start with /')}
          disabled={loading}
          ref={inputRef}
          tabIndex={0}
        />

        {autocompleteSuggestion && (
          <AutocompleteText style={{ left: `${11 + getTextWidth(question)}px` }}>
            {getAutocompleteDisplayText(autocompleteSuggestion, question)}
          </AutocompleteText>
        )}
      </div>
      <Button
        onClick={isRecording ? stopRecording : startRecording}
        disabled={loading}
        title={
          isRecording
            ? utils.app.textTranslated('stopRecording', t, 'Stop recording')
            : utils.app.textTranslated('startVoiceInput', t, 'Start voice input')
        }
      >
        {isRecording ? <MicrophoneIconFillled width={20} height={17} /> : <MicrophoneIcon width={20} height={17} />}
      </Button>
      {loading ? (
        <Button onClick={abort || undefined} title={utils.app.textTranslated('stopRequest', t, 'Stop request')}>
          <SquareIcon width={16} height={16} />
        </Button>
      ) : (
        <Button
          onClick={handleAsk}
          disabled={loading || !question}
          title={
            question
              ? utils.app.textTranslated('sendMessage', t, 'Send message')
              : utils.app.textTranslated('typeAMessageToSend', t, 'Type a message to send')
          }
        >
          {loading || !question ? <PlaneIcon width={18} height={18} /> : <PlaneIconFilled width={18} height={18} />}
        </Button>
      )}
    </InputRowContainer>
  );
};

export default InputRowComponent;
